import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { LandingCarrusel } from '@/components/LandingCarrusel';
import { LandingMenu } from '@/components/LandingMenu';
import { LandingTestimonios } from '@/components/LandingTestimonios';

const API = process.env.REACT_APP_BACKEND_URL;
const WA = '50254307807';
const AZUL = '#1FA3E8';
const A = 'https://customer-assets-lqy194kg.emergentagent.net/job_social-crm-3/artifacts';
const S = 'https://static.prod-images.emergentagent.com/jobs/f8421f9d-b7ab-4b05-852e-30567b337ae5/images';

const IMG = {
  logo: `${A}/ckf7btpo_c3217efc-1851-4a8d-8daf-e535de3c5dd6.jpeg`,
  hero: `${A}/rsygp8ts_a514dc3e-6ea5-49aa-bba7-fda2fb191965.jpeg`,
  mamparas: `${A}/zeyawc64_c229e5a3-9ac5-4388-81a7-4a05a13c55fe.jpeg`,
  cubiculos: `${A}/8eoadlve_0babb8c5-2643-4166-961c-6552e8d2a3d0.jpeg`,
  elevadores: `${A}/7wmexrd6_51ea4433-fbdc-4717-a3f1-4a3e26d6e5e1.jpeg`,
  mesa: `${S}/e414df6d3e2659c26ed9d64f17196fa421eea41335b5440fa596528acb4892a7.jpeg`,
  soldador: `${S}/68126ee89cab1e7b63ac96469acf787c5a8d9ded5665c68d93f7761fc9412c60.jpeg`,
  medida: `${S}/8026de66c93389cad89b94919a58fd9b5f07e4937e77d8c22e40b46a76948f81.jpeg`,
  cubOscuro: `${A}/tlgukj8b_172bdcfe-3d36-42a9-abac-b2d41111323e.jpeg`,
};

const CARRUSEL_INICIAL = [
  { url: IMG.hero, caption: 'Cubículos sanitarios de acero inoxidable' },
  { url: IMG.mamparas, caption: 'Divisiones de mingitorios en acero inox' },
  { url: IMG.elevadores, caption: 'Forros de elevadores en acero inoxidable' },
];

const DEPARTAMENTOS = ['Guatemala', 'Sacatepéquez', 'Chimaltenango', 'Escuintla', 'Santa Rosa', 'Sololá', 'Totonicapán', 'Quetzaltenango', 'Suchitepéquez', 'Retalhuleu', 'San Marcos', 'Huehuetenango', 'Quiché', 'Baja Verapaz', 'Alta Verapaz', 'Petén', 'Izabal', 'Zacapa', 'Chiquimula', 'Jalapa', 'Jutiapa', 'El Progreso'];
const waLink = (t) => `https://wa.me/${WA}?text=${encodeURIComponent(t)}`;

const TRABAJOS = [
  ['Mamparas y cubículos sanitarios', 'Divisiones de baños y mingitorios para centros comerciales, oficinas, restaurantes e instituciones. Resistentes al uso rudo y fáciles de desinfectar.', 'mamparas'],
  ['Forros de elevadores', 'Revestimiento de puertas, marcos y cabinas de elevadores. Acabado profesional que soporta el tráfico diario sin oxidarse.', 'elevadores'],
  ['Mesas de trabajo', 'Mesas grado alimenticio para cocinas industriales, laboratorios y clínicas. Superficies higiénicas que cumplen normas sanitarias.', 'mesa'],
  ['Cualquier trabajo a la medida', 'Barras, mostradores, lavatrastos, estanterías, protecciones, tolvas y más. Si lo imaginas en acero inoxidable, lo fabricamos.', 'medida'],
];

const ALEACIONES = [
  ['201', 'Económica y resistente', 'Ideal para interiores: mamparas, divisiones, forros y mobiliario en ambientes secos. Excelente relación calidad-precio.'],
  ['304', 'Grado alimenticio y médico', 'La más versátil y usada del mundo. Máxima resistencia a la corrosión, apta para cocinas, hospitales y exteriores.'],
  ['430', 'Uso general y forros', 'Muy usada en cocinas y revestimientos. Superficie brillante, fácil de limpiar y de excelente costo.'],
];

const INDUSTRIAS = [
  ['🏗️', 'Construcción', 'Mamparas sanitarias, forros de elevadores, pasamanos, protecciones y detalles arquitectónicos para edificios y comercios.'],
  ['🍽️', 'Alimenticia', 'Mesas, mostradores, lavatrastos y superficies grado alimenticio que cumplen normas de higiene y facilitan la desinfección.'],
  ['⚕️', 'Médica', 'Mobiliario y superficies para clínicas, laboratorios y hospitales: no porosas, asépticas y resistentes a desinfectantes fuertes.'],
];

export default function LandingAcero() {
  const [form, setForm] = useState({ nombre: '', telefono: '', departamento: '', detalles: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.post(`${API}/api/landing/track`, { producto: 'acero_inox_trabajos' }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim() || !form.departamento) { toast.error('Completa tu nombre, teléfono y departamento'); return; }
    try {
      setSending(true);
      const res = await axios.post(`${API}/api/landing/acero/lead`, form);
      setDone(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando, intenta de nuevo');
    } finally { setSending(false); }
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="min-h-screen bg-[#080B10] text-white">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 bg-[#080B10]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="Soluciones Arquitectónicas" className="h-10 sm:h-11 w-auto rounded" data-testid="header-logo" />
            <p className="hidden sm:block text-[10px] text-white/50 tracking-[0.25em] uppercase border-l border-white/15 pl-3">Modernizando ambientes</p>
          </div>
          <div className="flex items-center gap-2">
            <LandingMenu actual="/aceroinox" />
            <a href={waLink('¡Hola! Vi la página de trabajos de acero inoxidable y quiero una cotización 😊')} target="_blank" rel="noreferrer" data-testid="wa-header-btn"
              className="bg-[#25D366] hover:brightness-110 text-white text-sm font-bold px-4 py-2 rounded-full transition-all">WhatsApp</a>
          </div>
        </div>
      </header>

      <section className="relative">
        <img src={IMG.hero} alt="Trabajos de acero inoxidable" className="w-full h-[64vh] object-cover object-center opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080B10] via-[#080B10]/45 to-[#080B10]/25" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-4 pb-10 w-full">
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">Higiene, durabilidad y elegancia</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-3xl">Trabajos de <span style={{ color: AZUL }}>Acero Inoxidable</span></h1>
            <p className="text-white/70 text-base mt-4 max-w-xl">Mamparas sanitarias, forros de elevadores, mesas de trabajo y cualquier proyecto a la medida. Fabricación propia con láminas 201, 304 y 430 para la industria de la construcción, alimenticia y médica.</p>
            <div className="flex gap-3 mt-6 flex-wrap">
              <a href="#cotizar" data-testid="hero-cta" style={{ background: AZUL }} className="hover:brightness-110 text-white font-black px-6 py-3 rounded-full text-sm transition-all">COTIZA GRATIS →</a>
              <a href={waLink('¡Hola! Quiero cotizar un trabajo de acero inoxidable 😊')} target="_blank" rel="noreferrer" className="border border-white/25 hover:border-white/60 text-white font-bold px-6 py-3 rounded-full text-sm transition-colors">Escríbenos por WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: AZUL }} data-testid="stats-strip">
        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[['100% higiénico', 'superficie no porosa, fácil de desinfectar'], ['3 aleaciones', 'láminas 201, 304 y 430'], ['3 industrias', 'construcción, alimenticia y médica'], ['+18 años', 'de experiencia fabricando']].map(([n, t]) => (
            <div key={n}><p className="text-2xl sm:text-3xl font-black leading-none">{n}</p><p className="text-white/90 text-xs sm:text-sm font-medium mt-1">{t}</p></div>
          ))}
        </div>
      </section>

      {/* Por qué acero inox */}
      <section className="max-w-6xl mx-auto px-4 py-14" data-testid="seccion-porque">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <figure className="rounded-3xl overflow-hidden border border-white/10 order-2 lg:order-1">
            <img src={IMG.soldador} alt="Soldador profesional trabajando acero inoxidable" className="w-full h-[400px] object-cover object-center" />
            <figcaption className="bg-black/60 text-xs text-white/70 px-4 py-2 font-bold">Fabricación propia con soldadores especializados en acero inoxidable</figcaption>
          </figure>
          <div className="order-1 lg:order-2">
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">El material más confiable</p>
            <h2 className="text-2xl sm:text-3xl font-black leading-tight">¿Por qué <span style={{ color: AZUL }}>acero inoxidable</span>?</h2>
            <div className="space-y-4 mt-6">
              {[
                ['Higiénico por naturaleza', 'Su superficie no porosa no acumula bacterias, hongos ni olores. Por eso es el material exigido en cocinas, hospitales y laboratorios.'],
                ['Limpieza en segundos', 'Agua, jabón neutro y un paño: eso es todo. Soporta desinfectantes y lavados constantes sin dañarse ni mancharse.'],
                ['No se oxida ni se degrada', 'Resiste humedad, químicos de limpieza y uso rudo por décadas. Una inversión que no hay que reemplazar.'],
                ['Elegante y moderno', 'El acabado satinado o brillante da imagen profesional a baños públicos, elevadores y áreas de trabajo.'],
              ].map(([t, d], i) => (
                <div key={t} className="flex gap-3" data-testid={`razon-${i}`}>
                  <p style={{ color: AZUL }} className="text-xl font-black leading-none">{String(i + 1).padStart(2, '0')}</p>
                  <div><p className="font-black text-sm sm:text-base">{t}</p><p className="text-white/55 text-sm mt-1 leading-relaxed">{d}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trabajos */}
      <section className="max-w-6xl mx-auto px-4 pb-14" data-testid="seccion-trabajos">
        <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2 text-center">Lo que fabricamos</p>
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-3">Cualquier trabajo, <span style={{ color: AZUL }}>a la medida</span></h2>
        <p className="text-center text-white/60 text-sm mb-8 max-w-2xl mx-auto" data-testid="cobertura-gt-sv">
          🇬🇹🇸🇻 Hemos hecho trabajos de acero inoxidable en toda <span className="text-white font-bold">Guatemala</span> y <span className="text-white font-bold">El Salvador</span>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {TRABAJOS.map(([t, d, img], i) => (
            <figure key={t} className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] group" data-testid={`trabajo-${i}`}>
              <img src={IMG[img]} alt={t} className="w-full h-64 object-contain bg-black/40 group-hover:scale-105 transition-transform duration-500" />
              <figcaption className="p-5"><p className="font-black text-base">{t}</p><p className="text-white/55 text-sm mt-1.5">{d}</p></figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Aleaciones */}
      <section className="max-w-6xl mx-auto px-4 pb-14" data-testid="seccion-aleaciones">
        <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2 text-center">Te asesoramos para elegir</p>
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Aleaciones de <span style={{ color: AZUL }}>lámina</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ALEACIONES.map(([num, t, d]) => (
            <div key={num} className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 hover:border-[#1FA3E8]/40 transition-colors" data-testid={`aleacion-${num}`}>
              <p style={{ color: AZUL }} className="text-4xl font-black">{num}</p>
              <p className="font-black text-sm mt-2">{t}</p>
              <p className="text-white/55 text-sm mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Industrias */}
      <section className="max-w-6xl mx-auto px-4 pb-14" data-testid="seccion-industrias">
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 sm:p-8">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Soluciones para <span style={{ color: AZUL }}>3 industrias</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {INDUSTRIAS.map(([icon, t, d]) => (
              <div key={t} className="text-center" data-testid={`industria-${t.toLowerCase()}`}>
                <p className="text-4xl mb-3">{icon}</p>
                <p className="font-black text-base">{t}</p>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carrusel */}
      <section className="pb-14" data-testid="carrusel-proyectos">
        <div className="max-w-6xl mx-auto px-4 flex items-end justify-between gap-4">
          <div>
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2">Proyectos reales</p>
            <h2 className="text-2xl sm:text-3xl font-black mb-6">Trabajos <span style={{ color: AZUL }}>entregados</span></h2>
          </div>
          <p className="text-white/60 text-sm font-bold mb-6 shrink-0">Deslizar <span style={{ color: AZUL }} className="font-black text-lg">→</span></p>
        </div>
        <LandingCarrusel producto="acero_inox_trabajos" initial={CARRUSEL_INICIAL} />
      </section>

      {/* Manual */}
      <section className="max-w-6xl mx-auto px-4 pb-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">¡Somos Profesionales!</p>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight">Garantía con <span style={{ color: AZUL }}>respaldo</span></h2>
          <p className="text-white/60 text-base mt-4">El acero inoxidable dura décadas con cuidados mínimos. Sigue nuestro manual y tu garantía queda cubierta.</p>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 mt-5" data-testid="guia-card">
            <p className="font-black text-sm">📘 Manual de Mantenimiento GRATIS</p>
            <p className="text-white/55 text-sm mt-1.5">Descarga nuestro manual con la limpieza correcta del acero inoxidable, qué productos evitar y los cuidados que lo mantienen impecable por décadas.</p>
            <a href={`${API}/api/landing/guia-acero-trabajos`} data-testid="guia-download-btn"
              className="inline-block mt-3 border border-[#1FA3E8] text-[#1FA3E8] hover:bg-[#1FA3E8] hover:text-white font-black px-5 py-2.5 rounded-full text-xs transition-colors">DESCARGAR PDF</a>
          </div>
        </div>
        <figure className="rounded-3xl overflow-hidden border border-white/10">
          <img src={IMG.cubiculos} alt="Cubículos sanitarios de acero inoxidable" className="w-full h-[380px] object-cover object-center" />
          <figcaption className="bg-black/60 text-xs text-white/70 px-4 py-2 font-bold">Cubículos sanitarios: higiene y resistencia al uso rudo</figcaption>
        </figure>
      </section>

      {/* Testimonios */}
      <section className="max-w-6xl mx-auto px-4 pb-14" data-testid="seccion-testimonios">
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Clientes que confiaron <span style={{ color: AZUL }}>en nosotros</span></h2>
        <LandingTestimonios producto="acero_inox_trabajos" color={AZUL} initial={[
          { nombre: 'Ing. Marco Salguero', texto: 'Nos fabricaron las mamparas sanitarias de un centro comercial completo. Calidad de primera, instalación rápida y a los dos años siguen como nuevas.' },
          { nombre: 'Restaurante Doña Carmen', texto: 'Las mesas de trabajo de la cocina son grado alimenticio de verdad. Sanidad nos felicitó en la última inspección. Excelente servicio.' },
          { nombre: 'Constructora GTM', texto: 'Forraron los elevadores de nuestro edificio de oficinas. El acabado quedó impecable y soporta el tráfico diario sin una sola mancha de óxido.' },
        ]} />
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 pb-14" data-testid="seccion-faq">
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Preguntas <span style={{ color: AZUL }}>frecuentes</span></h2>
        <div className="space-y-3">
          {[
            ['¿Qué aleación me conviene?', 'Depende del uso: 201 para interiores y mobiliario general, 304 para cocinas, hospitales y exteriores (grado alimenticio y médico), y 430 para forros y revestimientos. Te asesoramos gratis según tu proyecto y presupuesto.'],
            ['¿El acero inoxidable se oxida?', 'Con el cuidado correcto, no. Su capa de cromo se regenera sola. Solo hay que evitar limpiadores con cloro y fibras metálicas que la dañan. Todo viene explicado en nuestro manual gratuito.'],
            ['¿Hacen trabajos a la medida?', 'Sí, cualquier trabajo: mamparas, forros, mesas, barras, mostradores, lavatrastos, estanterías, protecciones y más. Vamos, medimos y fabricamos exactamente lo que necesitas.'],
            ['¿Atienden proyectos grandes?', 'Sí. Trabajamos con constructoras, restaurantes, hospitales y centros comerciales. Manejamos volúmenes de obra completa con nuestro propio equipo de fabricación e instalación.'],
            ['¿Cómo empiezo mi proyecto?', 'Llena el formulario de esta página o escríbenos al WhatsApp 5430-7807. Te cotizamos gratis y sin compromiso.'],
          ].map(([q, a], i) => (
            <details key={q} className="group bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer px-5 py-4 font-bold text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden hover:bg-white/[0.03] transition-colors">{q}<span style={{ color: AZUL }} className="text-xl font-black shrink-0 group-open:rotate-45 transition-transform">+</span></summary>
              <p className="px-5 pb-5 text-white/65 text-sm leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Formulario */}
      <section id="cotizar" className="max-w-2xl mx-auto px-4 pb-16">
        <div className="bg-white/[0.05] border border-white/10 rounded-3xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-6" data-testid="lead-success">
              <p className="text-5xl mb-4">✅</p>
              <h3 className="text-2xl font-black">¡Recibido, {form.nombre.split(' ')[0]}!</h3>
              <p className="text-white/60 text-sm mt-2 mb-6">Un asesor te contactará muy pronto. Si quieres respuesta inmediata, continúa por WhatsApp:</p>
              <a href={done.wa_link} target="_blank" rel="noreferrer" data-testid="lead-wa-btn" className="inline-block bg-[#25D366] hover:brightness-110 text-white font-black px-8 py-3 rounded-full text-sm transition-all">💬 Continuar en WhatsApp</a>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-black">Cotiza tu trabajo de acero inox <span style={{ color: AZUL }}>GRATIS</span></h2>
              <p className="text-white/50 text-sm mt-1 mb-6">Sin compromiso. Te contactamos por WhatsApp o llamada.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Tu nombre *</label>
                  <input value={form.nombre} onChange={set('nombre')} data-testid="lead-nombre" placeholder="Ej: Carlos Pérez" className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Teléfono / WhatsApp *</label>
                  <input value={form.telefono} onChange={set('telefono')} data-testid="lead-telefono" placeholder="Ej: 5555 5555" inputMode="tel" className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">¿En qué departamento estás? *</label>
                  <select value={form.departamento} onChange={set('departamento')} data-testid="lead-departamento" style={{ colorScheme: 'dark' }} className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors">
                    <option value="">Selecciona…</option>
                    {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Detalles de tu proyecto</label>
                  <textarea value={form.detalles} onChange={set('detalles')} data-testid="lead-detalles" rows={3} placeholder="Ej: 6 mamparas sanitarias y 2 mesas de trabajo grado alimenticio…" className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors resize-none" />
                </div>
                <button type="submit" disabled={sending} data-testid="lead-submit" style={{ background: AZUL }} className="w-full hover:brightness-110 disabled:opacity-60 text-white font-black py-3.5 rounded-xl text-sm transition-all">{sending ? 'Enviando…' : 'QUIERO MI COTIZACIÓN GRATIS'}</button>
              </form>
            </>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        Soluciones Arquitectónicas · Modernizando ambientes · Grupo SACH · Guatemala · WhatsApp +502 5430 7807 · +18 años de experiencia
      </footer>

      <a href={waLink('¡Hola! Vi la página de trabajos de acero inoxidable y quiero una cotización 😊')} target="_blank" rel="noreferrer" data-testid="wa-float-btn" aria-label="WhatsApp"
        className="fixed bottom-5 right-5 z-50 bg-[#25D366] hover:scale-110 transition-transform w-14 h-14 rounded-full flex items-center justify-center shadow-2xl">
        <svg viewBox="0 0 32 32" width="30" height="30" fill="#fff"><path d="M16 3C9.4 3 4 8.4 4 15c0 2.1.6 4.2 1.7 6L4 29l8.2-1.6c1.2.6 2.5.9 3.8.9 6.6 0 12-5.4 12-12S22.6 3 16 3zm0 22c-1.2 0-2.4-.3-3.5-.8l-.6-.3-4.9 1 1-4.7-.3-.6C6.6 18.1 6 16.6 6 15 6 9.5 10.5 5 16 5s10 4.5 10 10-4.5 10-10 10zm5.5-7.5c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6l-1-2.3c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.4z"/></svg>
      </a>
    </div>
  );
}
